import { useEffect, useRef } from 'react';
import { useEditor } from '../../state/editorStore';
import { playbackClock } from '../../playback/clock';

/**
 * Plays timed sound-effects during preview: one hidden <audio> per SFX clip,
 * started at its startMs (from its own 0) and stopped after its durationMs, at
 * master × the clip's volume. One-shot (no loop). Kept in sync with the clock.
 */
export function SfxPlayer() {
  const sfx = useEditor((s) => s.audio.sfx);
  const masterVolume = useEditor((s) => s.masterVolume);
  const masterMuted = useEditor((s) => s.masterMuted);
  const els = useRef<Map<string, HTMLAudioElement>>(new Map());
  const model = useRef({ sfx, masterVolume, masterMuted });
  model.current = { sfx, masterVolume, masterMuted };

  useEffect(() => {
    const apply = (t: number, playing: boolean) => {
      const { sfx: clips, masterMuted: mm, masterVolume: mv } = model.current;
      const master = mm ? 0 : mv;
      for (const clip of clips) {
        const a = els.current.get(clip.id);
        if (!a || !clip.url) continue;
        const start = clip.startMs;
        const end = start + clip.durationMs;
        const inWindow = t >= start && t < end;
        if (playing && inWindow) {
          a.volume = Math.max(0, Math.min(1, master * clip.volume));
          const target = (t - start) / 1000;
          if (a.paused) {
            a.currentTime = Math.max(0, target);
            void a.play().catch(() => undefined);
          } else if (Math.abs(a.currentTime - target) > 0.35) {
            // Only re-sync on a real jump (a scrub) - not every frame.
            a.currentTime = Math.max(0, target);
          }
        } else if (!a.paused) {
          a.pause();
        }
      }
    };
    const unsub = playbackClock.subscribe(apply);
    return unsub;
  }, []);

  return (
    <>
      {sfx.map((clip) => (
        <audio
          key={clip.id}
          ref={(el) => {
            if (el) els.current.set(clip.id, el);
            else els.current.delete(clip.id);
          }}
          src={clip.url || undefined}
          preload="auto"
          hidden
        />
      ))}
    </>
  );
}
